/**
 * ToastContext.tsx — global toast feedback channel.
 *
 * Owns: queued toast messages, severity routing (success / error / warning /
 *       info), optional action buttons, auto-dismiss timing.
 * Used by: every component that needs to surface user feedback (mutations,
 *       imports, route transitions). Consumed via the `useToast()` hook.
 * Depends on: the 2.0 tokens (theme/cssVariables.ts) and the shared motion
 *       file (theme/motion.css). Every toast is Design System 2.0's one band
 *       (D-017 B31, state.md Entry 531): a 12.5/700 message on a filled
 *       ground — green when something worked, paprika when it failed, carbon
 *       otherwise — with a diamond, an optional Undo and a draining line,
 *       bottom-centre, at most three at once. The 13.5 bottom-right band retired.
 * Notes: Frozen file (operating-rules.md §4) — regressions hide errors or
 *       create misleading success states. Edit only via frozen-file unlock.
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { AlertColor } from '@mui/material';
import { onPermissionDenied } from '../services/permissionDenied';

// The filled grounds and the label on each (D-043: `--on-paprika` and
// `--on-status-on-track` flip per mode so the label always reads).
export type ToastTone = 'ink' | 'good' | 'indigo' | 'paprika';
const TONES: Record<ToastTone, [string, string]> = {
  ink: ['var(--carbon)', 'var(--on-carbon)'],
  good: ['var(--status-on-track)', 'var(--on-status-on-track)'],
  indigo: ['var(--indigo)', 'var(--on-indigo)'],
  paprika: ['var(--paprika)', 'var(--on-paprika)'],
};
// A caller that names no tone gets its severity's: good = green, failed = paprika (B31).
const SEVERITY_TONE: Record<AlertColor, ToastTone> = { success: 'good', error: 'paprika', warning: 'ink', info: 'ink' };
const TOAST_MAX = 3;
// D-023, mirroring `--toast-plain` / `--toast-undo`: a toast with an action stays
// long enough to Undo.
const TOAST_PLAIN_MS = 2600;
const TOAST_ACTION_MS = 6000;
const TOAST_LEAVE_MS = 250; // `--dur-lift`

type ToastOptions = {
  action?: { label: string; onClick: () => void };
  duration?: number;
  /** A second, lighter line after the message. */
  detail?: string;
  /** The filled ground; defaults to the severity's (success → good, error → paprika, else ink). */
  tone?: ToastTone;
  /** Kept for older callers: every toast is bottom-centre now. */
  placement?: 'center';
};

interface ToastMessage {
  id: number;
  message: string;
  severity: AlertColor;
  action?: { label: string; onClick: () => void };
  duration?: number;
  detail?: string;
  tone?: ToastTone;
}

interface ToastContextType {
  showToast: (message: string, severity?: AlertColor, opts?: ToastOptions) => void;
  success: (message: string, opts?: ToastOptions) => void;
  error: (message: string, opts?: ToastOptions) => void;
  warning: (message: string, opts?: ToastOptions) => void;
  info: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType>({
  showToast: () => {}, success: () => {}, error: () => {}, warning: () => {}, info: () => {},
});

export const useToast = () => useContext(ToastContext);

let nextId = 0;

/** One toast: rises in, drains its line from the next frame, fades at `dur`, leaves `TOAST_LEAVE_MS` later. */
const Toast: React.FC<{ t: ToastMessage; onClose: (id: number) => void }> = ({ t, onClose }) => {
  const dur = t.duration ?? (t.action ? TOAST_ACTION_MS : TOAST_PLAIN_MS);
  const tone = t.tone || SEVERITY_TONE[t.severity];
  const [bg, fg] = TONES[tone];
  const [draining, setDraining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setDraining(true));
    const fade = setTimeout(() => setLeaving(true), dur);
    const gone = setTimeout(() => onClose(t.id), dur + TOAST_LEAVE_MS);
    return () => { cancelAnimationFrame(raf); clearTimeout(fade); clearTimeout(gone); };
  }, [dur, t.id, onClose]);
  return (
    <div
      role="status"
      className="hub-toast-v2"
      data-tone={tone}
      style={{
        position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 9,
        background: bg, color: fg, font: '700 12.5px var(--font-head)', padding: '10px 15px',
        borderRadius: 'var(--r-control)',
        // a carbon band on a night page gets its hairline, so it doesn't sink into the ground
        boxShadow: tone === 'ink' ? 'inset 0 0 0 1px var(--rule-on-carbon), var(--toast)' : 'var(--toast)',
        maxWidth: 'min(560px, calc(100vw - 32px))', boxSizing: 'border-box',
        pointerEvents: 'auto', animation: 'ds-rise var(--dur-lift) var(--ease-out) both',
        ...(leaving ? { transition: 'opacity var(--dur-lift), transform var(--dur-lift)', opacity: 0, transform: 'translateY(6px)' } : {}),
      }}
    >
      <span aria-hidden style={{ width: 7, height: 7, transform: 'rotate(45deg)', background: 'currentColor', opacity: 0.85, flex: 'none' }} />
      <span style={{ minWidth: 0 }}>{t.message}</span>
      {t.detail ? <span style={{ fontWeight: 500, opacity: 0.72, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.detail}</span> : null}
      {t.action && (
        <button
          type="button"
          onClick={() => { t.action!.onClick(); onClose(t.id); }}
          style={{ flex: 'none', font: '700 11.5px var(--font-head)', color: 'inherit', background: 'rgba(255,255,255,.16)', border: 'none', borderRadius: 'var(--r-tag)', padding: '4px 9px', cursor: 'pointer' }}
        >
          {t.action.label}
        </button>
      )}
      <span aria-hidden style={{ position: 'absolute', left: 0, bottom: 0, height: 2, background: 'currentColor', opacity: 0.4, width: draining ? '0%' : '100%', transition: `width ${dur}ms linear` }} />
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, severity: AlertColor = 'info', opts?: ToastOptions) => {
    const id = nextId++;
    setToasts(prev => {
      const next = [...prev, { id, message, severity, action: opts?.action, duration: opts?.duration, detail: opts?.detail, tone: opts?.tone }];
      // at most three at once: the oldest go first
      return next.length <= TOAST_MAX ? next : next.slice(next.length - TOAST_MAX);
    });
  }, []);

  const handleClose = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const success = useCallback((msg: string, opts?: ToastOptions) => showToast(msg, 'success', opts), [showToast]);
  const error = useCallback((msg: string, opts?: ToastOptions) => showToast(msg, 'error', opts), [showToast]);
  const warning = useCallback((msg: string, opts?: ToastOptions) => showToast(msg, 'warning', opts), [showToast]);
  const info = useCallback((msg: string, opts?: ToastOptions) => showToast(msg, 'info', opts), [showToast]);

  /**
   * The app's single answer to "you are not allowed to do that".
   *
   * `apiClient`'s response interceptor announces every PERMISSION_DENIED 403
   * here, so the message names the switch and where to grant it — and appears
   * exactly once no matter how many components were mid-fetch. Duplicate
   * suppression is keyed on the permission, because one user action can fan
   * out several requests that all fail on the same missing grant; three
   * identical toasts would read as three separate problems.
   */
  const lastDenialRef = useRef<{ key: string; at: number } | null>(null);
  useEffect(() => onPermissionDenied((denial) => {
    const now = Date.now();
    const prev = lastDenialRef.current;
    if (prev && prev.key === denial.primary.key && now - prev.at < 4000) return;
    lastDenialRef.current = { key: denial.primary.key, at: now };
    showToast(denial.message, 'error', { duration: 9000 });
  }), [showToast]);

  const value = useMemo<ToastContextType>(() => ({
    showToast,
    success,
    error,
    warning,
    info,
  }), [showToast, success, error, warning, info]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 && (
        // Entry 235: toasts sit above every app overlay. 20200 clears the debug
        // HUD (20000) and the composer/modal stack (MUI's default 1400 hid them
        // behind the z-1500 email composer).
        <div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 20200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
          {toasts.map(t => <Toast key={t.id} t={t} onClose={handleClose} />)}
        </div>
      )}
    </ToastContext.Provider>
  );
};
